import { Button } from '@fluentui/react-button';
import { makeStyles, tokens, shorthands, Spinner } from '@fluentui/react-components';
import { Dialog, DialogActions, DialogBody, DialogContent, DialogSurface, DialogTitle } from '@fluentui/react-dialog';
import { DeleteRegular, WarningRegular } from '@fluentui/react-icons';

import { useChat } from '../../../../libs/hooks';
import { getFriendlyChatName } from '../../../../libs/hooks/useChat';
import { useAppSelector } from '../../../../redux/app/hooks';
import { customTokens } from '../../../../styles';
import { useState } from 'react';

const useClasses = makeStyles({
    root: {
        width: '500px',
        maxWidth: '90vw',
    },
    actions: {
        ...shorthands.gap(tokens.spacingHorizontalM),
        paddingTop: tokens.spacingVerticalL,
    },
    menuItem: {
        padding: tokens.spacingVerticalXS,
        minHeight: '32px',
    },
    deleteButton: {
        backgroundColor: customTokens.colorRedButton || tokens.colorPaletteRedBackground3,
        color: tokens.colorNeutralForegroundInverted,
        ':hover': {
            backgroundColor: tokens.colorPaletteRedBackground2,
        },
    },
    warningIcon: {
        color: tokens.colorPaletteRedForeground1,
        marginRight: tokens.spacingHorizontalXS,
    },
    content: {
        ...shorthands.padding(tokens.spacingVerticalM, 0),
        lineHeight: tokens.lineHeightBase300,
    },
    chatName: {
        fontWeight: tokens.fontWeightSemibold,
        color: tokens.colorBrandForeground1,
    },
});

interface IDeleteChatDialogProps {
    chatId: string;
    agentId?: string;
    isOpen?: boolean;
    onClose?: () => void;
    onSuccess?: (chatId: string) => void;
}

export const DeleteChatDialog: React.FC<IDeleteChatDialogProps> = ({
    chatId,
    agentId,
    isOpen = false,
    onClose,
    onSuccess,
}) => {
    const classes = useClasses();
    const chat = useChat();
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const { conversations } = useAppSelector((state) => state.conversations);
    const chatName = getFriendlyChatName(conversations[chatId]);

    const onDeleteChat = async () => {
        setLoading(true);
        setError(null);
        try {
            await chat.deleteChat(chatId, agentId);
            setSuccess(true);
            onSuccess?.(chatId);
        } catch (e: any) {
            setError('Sohbet silinirken bir hata oluştu. Lütfen tekrar deneyin.');
        } finally {
            setLoading(false);
        }
    };

    const handleCloseDialog = () => {
        setLoading(false);
        setSuccess(false);
        setError(null);
        onClose?.();
    };

    return (
        <Dialog
            open={isOpen}
            onOpenChange={(_event, data) => {
                if (!data.open) handleCloseDialog();
            }}
            modalType="alert"
        >
            <DialogSurface className={classes.root}>
                <DialogBody>
                    <DialogTitle>
                        <WarningRegular className={classes.warningIcon} />
                        Sohbeti Sil
                    </DialogTitle>
                    <DialogContent className={classes.content}>
                        {success ? (
                            <div style={{ textAlign: 'center', padding: '20px 0' }}>
                                ✅ <strong className={classes.chatName}>&ldquo;{chatName}&rdquo;</strong> adlı sohbet
                                başarıyla silindi.
                                <br />
                                <br />
                                <div
                                    style={{
                                        fontSize: '14px',
                                        color: tokens.colorNeutralForeground2,
                                        marginBottom: '16px',
                                    }}
                                >
                                    Sohbet listesi otomatik olarak güncellenecektir.
                                </div>
                                <Button appearance="primary" onClick={handleCloseDialog}>
                                    Kapat
                                </Button>
                            </div>
                        ) : (
                            <>
                                <strong className={classes.chatName}>&ldquo;{chatName}&rdquo;</strong> adlı sohbeti
                                silmek istediğinizden emin misiniz?
                                <br />
                                <br />
                                Bu eylem sohbeti, ilişkili tüm kaynakları ve belleği kalıcı olarak silecektir. Bu işlem
                                geri alınamaz.
                                {error && <div style={{ color: 'red', marginTop: 12 }}>{error}</div>}
                            </>
                        )}
                    </DialogContent>
                    {!success && (
                        <DialogActions className={classes.actions}>
                            <Button appearance="secondary" onClick={handleCloseDialog} disabled={loading}>
                                İptal
                            </Button>
                            <Button
                                appearance="primary"
                                className={classes.deleteButton}
                                onClick={() => {
                                    void onDeleteChat();
                                }}
                                disabled={loading}
                            >
                                {loading ? (
                                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                                        <Spinner size="tiny" />
                                        Siliniyor
                                    </span>
                                ) : (
                                    <>
                                        <DeleteRegular style={{ marginRight: '4px' }} />
                                        Sil
                                    </>
                                )}
                            </Button>
                        </DialogActions>
                    )}
                </DialogBody>
            </DialogSurface>
        </Dialog>
    );
};
